import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { Dumbbell, Apple, Plus, Pencil, Trash2, X, Link, ShieldCheck } from 'lucide-react';
import TargetAudienceSelector from '../components/TargetAudienceSelector';
import AttachmentsField, { AttachmentsView } from '../components/AttachmentsField';
import FootballTrainingPlansTab from '../components/FootballTrainingPlansTab';
import AthleteSelectorHeader from '../components/AthleteSelectorHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROGRAM_TYPE_CONFIG = {
  strength: { label: 'Strength', color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200' },
  conditioning: { label: 'Conditioning', color: 'text-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200' },
  power_plyometrics: { label: 'Power / Plyo', color: 'text-orange-600', bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200' },
  speed_agility: { label: 'Speed / Agility', color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200' },
  rehab: { label: 'Rehab', color: 'text-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200' },
};

const NUTRITION_TYPE_CONFIG = {
  daily: { label: 'Daily Plan' }, weekly: { label: 'Weekly Plan' },
  pre_training: { label: 'Pre-Training' }, post_training: { label: 'Post-Training' },
  recovery: { label: 'Recovery' }, match_day: { label: 'Match Day' }, general: { label: 'General' },
};

const OBJECTIVES = ['Hypertrophy', 'Strength', 'Power', 'Endurance', 'Recovery', 'Explosive Power', 'Injury Rehabilitation', 'Return to Play', 'Speed Development', 'Agility', 'General Fitness', 'Other'];
const PHASES = ['preparation', 'strength', 'power', 'peak', 'recovery', 'transition'];

const PerformancePrograms = () => {
  const { user, hasModuleAccess } = useAuth();
  const isStaffOrAdmin = user?.role === 'Admin' || user?.role === 'Staff';

  // Phase 75 — granular submodule visibility (backend enforces, this is UX).
  const canViewSnc       = hasModuleAccess('training_programs.snc', 'view');
  const canEditSnc       = hasModuleAccess('training_programs.snc', 'edit');
  const canDeleteSnc     = hasModuleAccess('training_programs.snc', 'delete');
  const canViewNutrition   = hasModuleAccess('training_programs.nutrition', 'view');
  const canEditNutrition   = hasModuleAccess('training_programs.nutrition', 'edit');
  const canDeleteNutrition = hasModuleAccess('training_programs.nutrition', 'delete');
  const canViewFootball    = hasModuleAccess('training_programs.football', 'view');
  const canEditFootball    = hasModuleAccess('training_programs.football', 'edit');
  const canDeleteFootball  = hasModuleAccess('training_programs.football', 'delete');
  const [athletes, setAthletes] = useState([]);
  const [tags, setTags] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [nutritionPlans, setNutritionPlans] = useState([]);
  const [injuries, setInjuries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(canViewSnc ? 'sc' : canViewNutrition ? 'nutrition' : 'football');

  // Snap the active tab back to one the user can actually see, e.g. when
  // permissions are refreshed mid-session.
  useEffect(() => {
    if (activeTab === 'sc' && !canViewSnc) setActiveTab(canViewNutrition ? 'nutrition' : 'football');
    if (activeTab === 'nutrition' && !canViewNutrition) setActiveTab(canViewSnc ? 'sc' : 'football');
    if (activeTab === 'football' && !canViewFootball) setActiveTab(canViewSnc ? 'sc' : 'nutrition');
  }, [activeTab, canViewSnc, canViewNutrition, canViewFootball]);

  // Filters
  const [scFilter, setScFilter] = useState('all');
  const [nutritionFilter, setNutritionFilter] = useState('all');

  // Dialogs
  const [createScOpen, setCreateScOpen] = useState(false);
  const [createNutritionOpen, setCreateNutritionOpen] = useState(false);
  const [viewProgramOpen, setViewProgramOpen] = useState(false);
  const [selectedProgram, setSelectedProgram] = useState(null);
  // Edit mode — when set, the corresponding dialog behaves as "Edit" instead of "Create"
  const [editingScId, setEditingScId] = useState(null);
  const [editingNutritionId, setEditingNutritionId] = useState(null);

  // S&C Form
  const emptyExercise = { name: '', sets: '', reps: '', intensity: '', rest: '', notes: '' };
  const emptySc = { title: '', program_type: 'strength', objective: 'General Fitness', description: '', duration_weeks: 1, periodization_phase: '', exercises: [{ ...emptyExercise }], coaching_notes: '', target_type: 'custom', target_squads: [], target_athletes: [], target_tag_ids: [], linked_injury_id: '' };
  const [scForm, setScForm] = useState(emptySc);

  // Nutrition Form
  const emptyNutrition = { title: '', plan_type: 'general', description: '', meals: '', hydration: '', supplements: '', coaching_notes: '', target_type: 'custom', target_squads: [], target_athletes: [], target_tag_ids: [] };
  const [nutritionForm, setNutritionForm] = useState(emptyNutrition);

  // Phase 6 — athlete-first workflow.
  const [selectedAthleteId, setSelectedAthleteId] = useState('');
  // Phase 81 — accept ?athlete=<id> deep-link from Athlete 360 cards.
  const [searchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/athletes?status=active`).then(r => setAthletes(r.data)).catch(() => {});
    axios.get(`${API}/tags`).then(r => setTags(r.data || [])).catch(() => {});
    fetchInjuries();
  }, []);

  // Phase 81 — pre-select athlete from ?athlete=<id> deep-link.
  // Set unconditionally; visibility filter may strip contract fixtures.
  useEffect(() => {
    const aid = searchParams.get('athlete') || searchParams.get('athlete_id');
    if (aid) setSelectedAthleteId(aid);
  }, [searchParams]);

  // Phase 6 — refetch SC/Nutrition lists when the selected athlete changes.
  useEffect(() => {
    if (!selectedAthleteId) {
      setPrograms([]);
      setNutritionPlans([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      axios.get(`${API}/sc-programs?athlete_id=${selectedAthleteId}`)
        .then(r => setPrograms(r.data || []))
        .catch(() => setPrograms([])),
      axios.get(`${API}/nutrition-plans?athlete_id=${selectedAthleteId}`)
        .then(r => setNutritionPlans(r.data || []))
        .catch(() => setNutritionPlans([])),
    ]).finally(() => setLoading(false));
  }, [selectedAthleteId]);

  const fetchPrograms = async () => {
    if (!selectedAthleteId) return;
    try { const r = await axios.get(`${API}/sc-programs?athlete_id=${selectedAthleteId}`); setPrograms(r.data || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  };
  const fetchNutrition = async () => {
    if (!selectedAthleteId) return;
    try { const r = await axios.get(`${API}/nutrition-plans?athlete_id=${selectedAthleteId}`); setNutritionPlans(r.data || []); } catch { /* ignore */ }
  };
  const fetchInjuries = async () => {
    try { const r = await axios.get(`${API}/injuries`); setInjuries(r.data.filter(i => i.status !== 'Recovered')); } catch { /* ignore */ }
  };

  const handleCreateSc = async () => {
    if (!scForm.title) { toast.error('Title is required'); return; }
    try {
      const payload = { ...scForm, duration_weeks: parseInt(scForm.duration_weeks) || 1, exercises: scForm.exercises.filter(e => e.name.trim()).map(e => ({ ...e, sets: parseInt(e.sets) || null })) };
      await axios.post(`${API}/sc-programs`, payload);
      toast.success('S&C Program created');
      setCreateScOpen(false);
      setScForm(emptySc);
      fetchPrograms();
    } catch (err) { toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to create'); }
  };

  // Open the S&C dialog pre-filled for editing an existing program
  const openEditSc = (program) => {
    setEditingScId(program.id);
    setScForm({
      title: program.title || '',
      program_type: program.program_type || 'strength',
      objective: program.objective || 'General Fitness',
      description: program.description || '',
      duration_weeks: program.duration_weeks || 1,
      periodization_phase: program.periodization_phase || '',
      exercises: (program.exercises && program.exercises.length)
        ? program.exercises.map(e => ({
            name: e.name || '', sets: e.sets ?? '', reps: e.reps || '',
            intensity: e.intensity || '', rest: e.rest || '', notes: e.notes || '',
          }))
        : [{ ...emptyExercise }],
      coaching_notes: program.coaching_notes || '',
      target_type: program.target_type || 'custom',
      target_squads: program.target_squads || [],
      target_athletes: program.target_athletes || [],
      target_tag_ids: program.target_tag_ids || [],
      linked_injury_id: program.linked_injury_id || '',
      attachments: program.attachments || [],
    });
    setCreateScOpen(true);
  };

  const handleUpdateSc = async () => {
    if (!editingScId) return;
    if (!scForm.title) { toast.error('Title is required'); return; }
    try {
      const payload = {
        ...scForm,
        duration_weeks: parseInt(scForm.duration_weeks) || 1,
        exercises: scForm.exercises.filter(e => e.name.trim()).map(e => ({ ...e, sets: parseInt(e.sets) || null })),
      };
      await axios.put(`${API}/sc-programs/${editingScId}`, payload);
      toast.success('S&C Program updated');
      setCreateScOpen(false);
      setEditingScId(null);
      setScForm(emptySc);
      fetchPrograms();
    } catch (err) { toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to update'); }
  };

  const handleCreateNutrition = async () => {
    if (!nutritionForm.title) { toast.error('Title is required'); return; }
    try {
      await axios.post(`${API}/nutrition-plans`, nutritionForm);
      toast.success('Nutrition Plan created');
      setCreateNutritionOpen(false);
      setNutritionForm(emptyNutrition);
      fetchNutrition();
    } catch (err) { toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to create'); }
  };

  // Open the Nutrition dialog pre-filled for editing an existing plan
  const openEditNutrition = (plan) => {
    setEditingNutritionId(plan.id);
    setNutritionForm({
      title: plan.title || '',
      plan_type: plan.plan_type || 'general',
      description: plan.description || '',
      meals: plan.meals || '',
      hydration: plan.hydration || '',
      supplements: plan.supplements || '',
      coaching_notes: plan.coaching_notes || '',
      target_type: plan.target_type || 'custom',
      target_squads: plan.target_squads || [],
      target_athletes: plan.target_athletes || [],
      target_tag_ids: plan.target_tag_ids || [],
      attachments: plan.attachments || [],
    });
    setCreateNutritionOpen(true);
  };

  const handleUpdateNutrition = async () => {
    if (!editingNutritionId) return;
    if (!nutritionForm.title) { toast.error('Title is required'); return; }
    try {
      await axios.put(`${API}/nutrition-plans/${editingNutritionId}`, nutritionForm);
      toast.success('Nutrition Plan updated');
      setCreateNutritionOpen(false);
      setEditingNutritionId(null);
      setNutritionForm(emptyNutrition);
      fetchNutrition();
    } catch (err) { toast.error(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Failed to update'); }
  };

  const handleDeleteProgram = async (id) => {
    if (!window.confirm('Delete this program?')) return;
    try { await axios.delete(`${API}/sc-programs/${id}`); toast.success('Deleted'); fetchPrograms(); } catch { toast.error('Failed'); }
  };

  const handleDeleteNutrition = async (id) => {
    if (!window.confirm('Delete this plan?')) return;
    try { await axios.delete(`${API}/nutrition-plans/${id}`); toast.success('Deleted'); fetchNutrition(); } catch { toast.error('Failed'); }
  };

  const addExercise = () => setScForm({ ...scForm, exercises: [...scForm.exercises, { ...emptyExercise }] });
  const removeExercise = (i) => setScForm({ ...scForm, exercises: scForm.exercises.filter((_, idx) => idx !== i) });
  const updateExercise = (i, field, value) => {
    const exs = [...scForm.exercises];
    exs[i] = { ...exs[i], [field]: value };
    setScForm({ ...scForm, exercises: exs });
  };

  const filteredPrograms = scFilter === 'all' ? programs : programs.filter(p => p.program_type === scFilter);
  const filteredNutrition = nutritionFilter === 'all' ? nutritionPlans : nutritionPlans.filter(p => p.plan_type === nutritionFilter);

  const getTargetLabel = (item) => {
    if (item.target_type === 'all') return 'All Athletes';
    const parts = [...(item.target_squads || [])];
    if ((item.target_athletes || []).length > 0) parts.push(`${item.target_athletes.length} athlete${item.target_athletes.length > 1 ? 's' : ''}`);
    return parts.join(', ') || 'No target';
  };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8" data-testid="performance-programs-page">
        <div className="flex justify-between items-center mb-5">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100" style={{ fontFamily: 'Manrope, sans-serif' }}>Performance Programs</h2>
            <p className="text-gray-500 dark:text-gray-400 mt-0.5 text-sm">S&C programs, nutrition plans, and rehab prescriptions</p>
          </div>
        </div>

        <div className="mb-5">
          <AthleteSelectorHeader
            athletes={athletes}
            value={selectedAthleteId}
            onChange={setSelectedAthleteId}
            label="Athlete-Specific Programs"
            subtitle="Pick an athlete to see only their S&C programs, nutrition plans, and football training plans."
            placeholder="Search active athletes…"
            testIdPrefix="perf-programs-athlete"
          >

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-5" data-testid="training-programs-tabs">
            {canViewSnc && (
              <TabsTrigger value="sc" className="gap-1.5" data-testid="tab-sc">
                <Dumbbell className="w-3.5 h-3.5" /> S&C Programs
              </TabsTrigger>
            )}
            {canViewNutrition && (
              <TabsTrigger value="nutrition" className="gap-1.5" data-testid="tab-nutrition">
                <Apple className="w-3.5 h-3.5" /> Nutrition Plans
              </TabsTrigger>
            )}
            {canViewFootball && (
              <TabsTrigger value="football" className="gap-1.5" data-testid="tab-football">
                <ShieldCheck className="w-3.5 h-3.5" /> Football Training Plans
              </TabsTrigger>
            )}
          </TabsList>

          {!canViewSnc && !canViewNutrition && !canViewFootball && (
            <Card data-testid="no-programme-access">
              <CardContent className="py-12 text-center">
                <Dumbbell className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 text-sm">No programme access assigned.</p>
                <p className="text-gray-400 text-xs mt-1">
                  Ask an Admin to grant access in Access Control → Training Programs.
                </p>
              </CardContent>
            </Card>
          )}

          {/* ======== S&C PROGRAMS ======== */}
          <TabsContent value="sc">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <div className="flex gap-1.5 flex-wrap">
                {[{ v: 'all', l: 'All' }, ...Object.entries(PROGRAM_TYPE_CONFIG).map(([k, v]) => ({ v: k, l: v.label }))].map(t => (
                  <button key={t.v} onClick={() => setScFilter(t.v)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${scFilter === t.v ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'}`} data-testid={`sc-filter-${t.v}`}>{t.l}</button>
                ))}
              </div>
              {canEditSnc && <Button onClick={() => { setScForm(emptySc); setCreateScOpen(true); }} className="bg-pfap-green hover:bg-pfap-green/90 h-9" data-testid="create-sc-btn"><Plus className="w-4 h-4 mr-1" /> New Program</Button>}
            </div>

            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-600" /></div>
            ) : filteredPrograms.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><Dumbbell className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" /><p className="text-gray-500 text-sm">No S&C programs found</p></CardContent></Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="sc-programs-grid">
                {filteredPrograms.map(p => {
                  const cfg = PROGRAM_TYPE_CONFIG[p.program_type] || PROGRAM_TYPE_CONFIG.strength;
                  return (
                    <Card key={p.id} className={`border ${cfg.border} hover:shadow-md transition-shadow cursor-pointer`} onClick={() => { setSelectedProgram(p); setViewProgramOpen(true); }} data-testid={`sc-card-${p.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <Badge className={`text-[10px] px-1.5 py-0 ${cfg.bg} ${cfg.color} border ${cfg.border}`}>{cfg.label}</Badge>
                          {canEditSnc && (
                            <div className="flex items-center gap-0.5">
                              <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-gray-500 hover:text-gray-900" onClick={e => { e.stopPropagation(); openEditSc(p); }} data-testid={`edit-sc-${p.id}`}><Pencil className="w-3 h-3" /></Button>
                              {canDeleteSnc && (
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-500" onClick={e => { e.stopPropagation(); handleDeleteProgram(p.id); }} data-testid={`delete-sc-${p.id}`}><Trash2 className="w-3 h-3" /></Button>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">{p.title}</p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {p.objective && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{p.objective}</Badge>}
                          {p.periodization_phase && <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{p.periodization_phase}</Badge>}
                          {p.duration_weeks > 1 && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{p.duration_weeks}w</Badge>}
                          {p.linked_injury_id && <Badge className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-600 border-purple-200"><Link className="w-2.5 h-2.5 mr-0.5" />Rehab</Badge>}
                        </div>
                        <p className="text-[10px] text-gray-400">{getTargetLabel(p)} · {(p.exercises || []).length} exercises{(p.attachments || []).length > 0 ? ` · 📎 ${p.attachments.length}` : ''}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ======== NUTRITION PLANS ======== */}
          <TabsContent value="nutrition">
            <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
              <div className="flex gap-1.5 flex-wrap">
                {[{ v: 'all', l: 'All' }, ...Object.entries(NUTRITION_TYPE_CONFIG).map(([k, v]) => ({ v: k, l: v.label }))].map(t => (
                  <button key={t.v} onClick={() => setNutritionFilter(t.v)} className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${nutritionFilter === t.v ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200'}`} data-testid={`nutrition-filter-${t.v}`}>{t.l}</button>
                ))}
              </div>
              {canEditNutrition && <Button onClick={() => { setNutritionForm(emptyNutrition); setCreateNutritionOpen(true); }} className="bg-pfap-green hover:bg-pfap-green/90 h-9" data-testid="create-nutrition-btn"><Plus className="w-4 h-4 mr-1" /> New Plan</Button>}
            </div>

            {nutritionPlans.length === 0 ? (
              <Card><CardContent className="py-12 text-center"><Apple className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" /><p className="text-gray-500 text-sm">No nutrition plans found</p></CardContent></Card>
            ) : filteredNutrition.length === 0 ? (
              <Card><CardContent className="py-8 text-center"><p className="text-gray-500 text-sm">No plans match this filter</p></CardContent></Card>
            ) : (
              <div className="space-y-3" data-testid="nutrition-plans-list">
                {filteredNutrition.map(plan => (
                  <Card key={plan.id} className="hover:shadow-sm transition-shadow" data-testid={`nutrition-card-${plan.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{plan.title}</p>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{NUTRITION_TYPE_CONFIG[plan.plan_type]?.label || plan.plan_type}</Badge>
                          </div>
                          {plan.description && <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1 mb-1">{plan.description}</p>}
                          <p className="text-[10px] text-gray-400">{getTargetLabel(plan)} · By {plan.created_by_name}</p>
                          {(plan.attachments || []).length > 0 && (
                            <div className="mt-2"><AttachmentsView attachments={plan.attachments} data-testid={`nutrition-attachments-${plan.id}`} /></div>
                          )}
                        </div>
                        {canEditNutrition && (
                          <div className="flex items-center gap-0.5">
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-gray-500 hover:text-gray-900" onClick={() => openEditNutrition(plan)} data-testid={`edit-nutrition-${plan.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                            {canDeleteNutrition && (
                              <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteNutrition(plan.id)} data-testid={`delete-nutrition-${plan.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* ======== FOOTBALL TRAINING PLANS (Phase 75) ======== */}
          {canViewFootball && (
            <TabsContent value="football" data-testid="football-plans-tabcontent">
              <FootballTrainingPlansTab
                athletes={athletes}
                tags={tags}
                canEdit={canEditFootball}
                canDelete={canDeleteFootball}
                athleteId={selectedAthleteId}
              />
            </TabsContent>
          )}
        </Tabs>

          </AthleteSelectorHeader>
        </div>

        {/* ===== CREATE / EDIT S&C DIALOG ===== */}
        <Dialog open={createScOpen} onOpenChange={(open) => { setCreateScOpen(open); if (!open) { setEditingScId(null); setScForm(emptySc); } }}>
          <DialogContent className="max-w-2xl" data-testid="create-sc-dialog">
            <DialogHeader>
              <DialogTitle>{editingScId ? 'Edit S&C Program' : 'New S&C Program'}</DialogTitle>
              <DialogDescription>{editingScId ? 'Update the program details below' : 'Design and assign a training program'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <Label>Title *</Label>
                <Input value={scForm.title} onChange={e => setScForm({ ...scForm, title: e.target.value })} placeholder="e.g., Pre-Season Strength Phase" data-testid="sc-title" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select value={scForm.program_type} onValueChange={v => setScForm({ ...scForm, program_type: v })}>
                    <SelectTrigger data-testid="sc-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(PROGRAM_TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Objective</Label>
                  <Select value={scForm.objective} onValueChange={v => setScForm({ ...scForm, objective: v })}>
                    <SelectTrigger data-testid="sc-objective-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{OBJECTIVES.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Duration (weeks)</Label>
                  <Input type="number" min={1} max={52} value={scForm.duration_weeks} onChange={e => setScForm({ ...scForm, duration_weeks: e.target.value })} data-testid="sc-duration" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Periodization Phase</Label>
                  <Select value={scForm.periodization_phase || 'none'} onValueChange={v => setScForm({ ...scForm, periodization_phase: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="sc-phase-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {PHASES.map(p => <SelectItem key={p} value={p} className="capitalize">{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Link to Injury (Rehab)</Label>
                  <Select value={scForm.linked_injury_id || 'none'} onValueChange={v => setScForm({ ...scForm, linked_injury_id: v === 'none' ? '' : v })}>
                    <SelectTrigger data-testid="sc-injury-link"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No linked injury</SelectItem>
                      {injuries.map(inj => <SelectItem key={inj.id} value={inj.id}>{inj.injury_type} — {athletes.find(a => a.id === inj.athlete_id)?.full_name || inj.athlete_id}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Exercises */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label>Exercises</Label>
                  <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={addExercise} data-testid="add-exercise"><Plus className="w-3 h-3 mr-1" /> Add</Button>
                </div>
                <div className="space-y-2">
                  {scForm.exercises.map((ex, i) => (
                    <div key={i} className="flex gap-2 items-start p-2 border rounded-lg bg-gray-50 dark:bg-gray-800" data-testid={`exercise-row-${i}`}>
                      <Input className="flex-1 h-8 text-xs" placeholder="Exercise name" value={ex.name} onChange={e => updateExercise(i, 'name', e.target.value)} />
                      <Input className="w-16 h-8 text-xs" placeholder="Sets" value={ex.sets} onChange={e => updateExercise(i, 'sets', e.target.value)} />
                      <Input className="w-20 h-8 text-xs" placeholder="Reps" value={ex.reps} onChange={e => updateExercise(i, 'reps', e.target.value)} />
                      <Input className="w-24 h-8 text-xs" placeholder="Intensity" value={ex.intensity} onChange={e => updateExercise(i, 'intensity', e.target.value)} />
                      <Input className="w-16 h-8 text-xs" placeholder="Rest" value={ex.rest} onChange={e => updateExercise(i, 'rest', e.target.value)} />
                      <Button type="button" size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500" onClick={() => removeExercise(i)}><X className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <Label>Coaching Notes</Label>
                <Textarea value={scForm.coaching_notes} onChange={e => setScForm({ ...scForm, coaching_notes: e.target.value })} placeholder="Instructions, progression notes..." rows={2} data-testid="sc-notes" />
              </div>

              <TargetAudienceSelector
                athletes={athletes}
                tags={tags}
                value={{ target_type: scForm.target_type, target_squads: scForm.target_squads, target_athletes: scForm.target_athletes, target_tag_ids: scForm.target_tag_ids }}
                onChange={val => setScForm({ ...scForm, target_type: val.target_type, target_squads: val.target_squads, target_athletes: val.target_athletes, target_tag_ids: val.target_tag_ids })}
                label="Assign To" compact data-testid="sc-target"
              />

              <AttachmentsField
                value={scForm.attachments}
                onChange={val => setScForm({ ...scForm, attachments: val })}
                label="Resources"
                data-testid="sc-attachments"
              />
            </div>
            <DialogFooter>
              <Button onClick={editingScId ? handleUpdateSc : handleCreateSc} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="submit-sc">{editingScId ? 'Save Changes' : 'Create Program'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== CREATE / EDIT NUTRITION DIALOG ===== */}
        <Dialog open={createNutritionOpen} onOpenChange={(open) => { setCreateNutritionOpen(open); if (!open) { setEditingNutritionId(null); setNutritionForm(emptyNutrition); } }}>
          <DialogContent className="max-w-lg" data-testid="create-nutrition-dialog">
            <DialogHeader>
              <DialogTitle>{editingNutritionId ? 'Edit Nutrition Plan' : 'New Nutrition Plan'}</DialogTitle>
              <DialogDescription>{editingNutritionId ? 'Update the plan details below' : 'Create a structured nutrition plan'}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
              <div>
                <Label>Title *</Label>
                <Input value={nutritionForm.title} onChange={e => setNutritionForm({ ...nutritionForm, title: e.target.value })} placeholder="e.g., Match Day Nutrition" data-testid="nutrition-title" />
              </div>
              <div>
                <Label>Plan Type</Label>
                <Select value={nutritionForm.plan_type} onValueChange={v => setNutritionForm({ ...nutritionForm, plan_type: v })}>
                  <SelectTrigger data-testid="nutrition-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(NUTRITION_TYPE_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Meals / Diet Guidance</Label>
                <Textarea value={nutritionForm.meals} onChange={e => setNutritionForm({ ...nutritionForm, meals: e.target.value })} placeholder="Breakfast, Lunch, Dinner, Snacks..." rows={4} data-testid="nutrition-meals" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Hydration</Label>
                  <Textarea value={nutritionForm.hydration} onChange={e => setNutritionForm({ ...nutritionForm, hydration: e.target.value })} placeholder="Hydration guidance..." rows={2} data-testid="nutrition-hydration" />
                </div>
                <div>
                  <Label>Supplements</Label>
                  <Textarea value={nutritionForm.supplements} onChange={e => setNutritionForm({ ...nutritionForm, supplements: e.target.value })} placeholder="Recommended supplements..." rows={2} data-testid="nutrition-supplements" />
                </div>
              </div>
              <TargetAudienceSelector
                athletes={athletes}
                tags={tags}
                value={{ target_type: nutritionForm.target_type, target_squads: nutritionForm.target_squads, target_athletes: nutritionForm.target_athletes, target_tag_ids: nutritionForm.target_tag_ids }}
                onChange={val => setNutritionForm({ ...nutritionForm, target_type: val.target_type, target_squads: val.target_squads, target_athletes: val.target_athletes, target_tag_ids: val.target_tag_ids })}
                label="Assign To" compact data-testid="nutrition-target"
              />
              <AttachmentsField
                value={nutritionForm.attachments}
                onChange={val => setNutritionForm({ ...nutritionForm, attachments: val })}
                label="Resources"
                data-testid="nutrition-attachments"
              />
            </div>
            <DialogFooter>
              <Button onClick={editingNutritionId ? handleUpdateNutrition : handleCreateNutrition} className="bg-pfap-green hover:bg-pfap-green/90" data-testid="submit-nutrition">{editingNutritionId ? 'Save Changes' : 'Create Plan'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ===== VIEW PROGRAM DIALOG ===== */}
        <Dialog open={viewProgramOpen} onOpenChange={setViewProgramOpen}>
          <DialogContent className="max-w-2xl" data-testid="view-program-dialog">
            {selectedProgram && (() => {
              const cfg = PROGRAM_TYPE_CONFIG[selectedProgram.program_type] || PROGRAM_TYPE_CONFIG.strength;
              return (
                <>
                  <DialogHeader>
                    <DialogTitle>{selectedProgram.title}</DialogTitle>
                    <DialogDescription asChild>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-[10px] px-1.5 py-0 ${cfg.bg} ${cfg.color} border ${cfg.border}`}>{cfg.label}</Badge>
                        {selectedProgram.objective && <Badge variant="outline" className="text-[10px] px-1.5 py-0">{selectedProgram.objective}</Badge>}
                        {selectedProgram.periodization_phase && <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{selectedProgram.periodization_phase}</Badge>}
                        <span className="text-xs">{selectedProgram.duration_weeks}w · {getTargetLabel(selectedProgram)}</span>
                      </div>
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 max-h-[50vh] overflow-y-auto">
                    {selectedProgram.description && <p className="text-sm text-gray-600 dark:text-gray-400">{selectedProgram.description}</p>}
                    {(selectedProgram.exercises || []).length > 0 && (
                      <div>
                        <p className="text-xs font-medium text-gray-500 mb-2">Exercises ({selectedProgram.exercises.length})</p>
                        <div className="border rounded-lg overflow-hidden">
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 dark:bg-gray-800">
                              <tr>
                                <th className="text-left p-2 font-medium text-gray-500">Exercise</th>
                                <th className="text-center p-2 font-medium text-gray-500 w-14">Sets</th>
                                <th className="text-center p-2 font-medium text-gray-500 w-16">Reps</th>
                                <th className="text-center p-2 font-medium text-gray-500 w-20">Intensity</th>
                                <th className="text-center p-2 font-medium text-gray-500 w-14">Rest</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedProgram.exercises.map((ex, i) => (
                                <tr key={i} className="border-t">
                                  <td className="p-2 font-medium text-gray-800 dark:text-gray-200">{ex.name}</td>
                                  <td className="p-2 text-center text-gray-600">{ex.sets || '-'}</td>
                                  <td className="p-2 text-center text-gray-600">{ex.reps || '-'}</td>
                                  <td className="p-2 text-center text-gray-600">{ex.intensity || '-'}</td>
                                  <td className="p-2 text-center text-gray-600">{ex.rest || '-'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}
                    {selectedProgram.coaching_notes && (
                      <div className="p-3 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800">
                        <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1">Coaching Notes</p>
                        <p className="text-xs text-amber-600 dark:text-amber-300 whitespace-pre-wrap">{selectedProgram.coaching_notes}</p>
                      </div>
                    )}
                    {(selectedProgram.attachments || []).length > 0 && (
                      <AttachmentsView attachments={selectedProgram.attachments} data-testid="sc-detail-attachments" />
                    )}
                  </div>
                  {isStaffOrAdmin && (
                    <DialogFooter>
                      <Button variant="outline" onClick={() => { setViewProgramOpen(false); openEditSc(selectedProgram); }} data-testid="edit-from-view-btn"><Pencil className="w-3.5 h-3.5 mr-1" /> Edit Program</Button>
                    </DialogFooter>
                  )}
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default PerformancePrograms;
